import { Controller } from "@hotwired/stimulus";

/**
 * Stock Movement Form Controller
 * Handles the dynamic behavior of stock movement forms
 */
export default class extends Controller {
  static targets = [
    "category",
    "movementType",
    "itemsContainer",
    "itemTemplate",
    "itemRow",
    "destinationFields",
    "destinationLocation",
    "totalPrice",
    "signature",
    "signaturePreview",
  ];

  static values = {
    nextItemId: Number,
    itemCount: Number,
  };

  connect() {
    this.nextItemIdValue = this.nextItemIdValue || 0;
    this.itemCountValue = this.hasItemRowTargets
      ? this.itemRowTargets.length
      : 0;
    this.updateFormFields();
    this.calculateTotal();

    if (this.hasSignatureTarget && this.hasSignaturePreviewTarget) {
      this.setupSignature();
    }

    // Setup event delegation for stock item selection
    this.setupStockItemChangeListener();
  }

  setupStockItemChangeListener() {
    if (!this.hasItemsContainerTarget) return;

    // Event delegation - listen for all change events on selects within the container
    this.itemsContainerTarget.addEventListener('change', (event) => {
      const target = event.target;

      // Check if the changed element is a stock_item_id select
      if (target.matches('select[name*="[stock_item_id]"]')) {
        this.updatePriceFromSelect(target);
      }
    });
  }

  updateFormFields() {
    const category = this.categoryTarget.value;

    // Hide/show fields based on category
    if (this.hasDestinationFieldsTarget) {
      if (category === "transfer") {
        this.destinationFieldsTarget.classList.remove("hidden");
        this.destinationLocationTargets.forEach((field) => {
          field.setAttribute("required", "required");
        });
      } else {
        this.destinationFieldsTarget.classList.add("hidden");
        this.destinationLocationTargets.forEach((field) => {
          field.removeAttribute("required");
        });
      }
    }
  }

  categoryChanged() {
    this.updateFormFields();
  }

  addItem(event) {
    event.preventDefault();

    const template = this.itemTemplateTarget.innerHTML;
    const newItem = template.replace(/NEW_RECORD/g, this.nextItemIdValue);

    this.itemsContainerTarget.insertAdjacentHTML("beforeend", newItem);
    this.nextItemIdValue++;
    this.itemCountValue++;
  }

  removeItem(event) {
    event.preventDefault();

    const item = event.target.closest(
      '[data-stock-movement-form-target="itemRow"]'
    );

    // Check if this is a persisted record and mark for destruction instead of removing
    const destroyInput = item.querySelector('input[name*="[_destroy]"]');
    if (destroyInput) {
      destroyInput.value = "1";
      item.style.display = "none";
    } else {
      item.remove();
    }

    this.itemCountValue--;
    this.calculateTotal();
  }

  calculateTotal() {
    if (!this.hasTotalPriceTarget) return;

    let total = 0;

    this.itemRowTargets.forEach((row) => {
      if (row.style.display !== "none") {
        const quantity =
          parseFloat(row.querySelector('input[name*="[quantity]"]').value) || 0;
        const price =
          parseFloat(row.querySelector('input[name*="[unit_price]"]').value) ||
          0;
        total += quantity * price;
      }
    });

    // « 62 500 », not « 62500 » (QA3 SP3-A22): F CFA, no centimes.
    this.totalPriceTarget.textContent = Math.round(total)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }

  updateTotals() {
    this.calculateTotal();
  }

  async updatePriceFromSelect(stockItemSelect) {
    const stockItemId = stockItemSelect.value;

    if (!stockItemId) return;

    // Find the price input in the same row
    const row = stockItemSelect.closest('[data-stock-movement-form-target="itemRow"]');
    const priceInput = row.querySelector('input[name*="[unit_price]"]');

    if (!priceInput) return;

    try {
      const locale = document.documentElement.lang || 'fr';
      const response = await fetch(`/${locale}/schools/stocks/stock_items/${stockItemId}/get_price`);

      if (response.ok) {
        const data = await response.json();
        // « 2500 », not « 2500,0 » (QA3 SP3-A22).
        priceInput.value = Math.round(parseFloat(data.unit_price) || 0);
        this.calculateTotal();
      }
    } catch (error) {
      console.error('Error fetching stock item price:', error);
    }
  }

  setupSignature() {
    const canvas = this.signatureTarget;
    const preview = this.signaturePreviewTarget;
    const ctx = canvas.getContext("2d");
    let isDrawing = false;
    let lastX = 0;
    let lastY = 0;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Setup canvas for drawing
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#000";

    // Mouse events
    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("mouseout", stopDrawing);

    // Touch events for mobile
    canvas.addEventListener("touchstart", touchStartDrawing);
    canvas.addEventListener("touchmove", touchDraw);
    canvas.addEventListener("touchend", stopDrawing);

    function startDrawing(e) {
      isDrawing = true;
      lastX = e.offsetX;
      lastY = e.offsetY;
    }

    function touchStartDrawing(e) {
      e.preventDefault();
      const touch = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      lastX = touch.clientX - rect.left;
      lastY = touch.clientY - rect.top;
      isDrawing = true;
    }

    function draw(e) {
      if (!isDrawing) return;

      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(e.offsetX, e.offsetY);
      ctx.stroke();

      lastX = e.offsetX;
      lastY = e.offsetY;

      updatePreview();
    }

    function touchDraw(e) {
      if (!isDrawing) return;
      e.preventDefault();

      const touch = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      const currentX = touch.clientX - rect.left;
      const currentY = touch.clientY - rect.top;

      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(currentX, currentY);
      ctx.stroke();

      lastX = currentX;
      lastY = currentY;

      updatePreview();
    }

    function stopDrawing() {
      isDrawing = false;
      updatePreview();
    }

    function updatePreview() {
      // Convert canvas data to base64 image and set hidden input value
      const dataURL = canvas.toDataURL("image/png");
      preview.value = dataURL;
    }
  }

  clearSignature(event) {
    event.preventDefault();

    if (this.hasSignatureTarget) {
      const ctx = this.signatureTarget.getContext("2d");
      ctx.clearRect(
        0,
        0,
        this.signatureTarget.width,
        this.signatureTarget.height
      );

      if (this.hasSignaturePreviewTarget) {
        this.signaturePreviewTarget.value = "";
      }
    }
  }
}
